import { Box, Button, Grid, Link, Stack, Typography } from "@mui/material";
import Page from "../../components/Page"
import { deleteLibraryDate as deleteLibraryData, listData,  } from "../../master/libraryData";
import { SaveableBase } from "../../model/character";
import { NavigateFunction, Route, Routes, useNavigate } from "react-router-dom";
import Section from "../../components/Section";

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState } from "react";

import HexagonIcon from '@mui/icons-material/Hexagon';

import styled from 'styled-components';
import { WeaponBlockEditorPage } from "./manage/WeaponBlockEditor";
import { OutfitBlockEditorPage } from "./manage/OutfitBlockEditor";
import { EquipmentEditor } from "./manage/EquipmentEditor";
import { TraitEditor } from "./manage/TraitEditor";
import { CompanionWeaponPage } from "./manage/CompanionWeaponEditor";
import { SpeciesEditor } from "./manage/SpeciesEditor";
import Import from "./Import";
import { PackManager } from "./manage/Pack";
import ConfirmDialog from "../../components/ConfirmDialog";

const Hexagon = styled.div`
    width: 55px;
    height: 55px;
    margin: 0 auto;
    position: relative;
`;

const HexagonInner = styled.div`
    position: absolute;
    top:0;
    left:0;
    right:0;
    bottom:0;
    font-size: 20px;
    line-height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff
`;

const LibraryItemInner = styled.div`
    display:flex;
    justify-content: space-between;
    align-items: center;
`;

interface LibraryItemProps {
    item: SaveableBase;
    path: string;
    navigate: NavigateFunction;
    onDelete: () => void;
}

const DeleteButton = (props:
    { 
        item: SaveableBase,
        path: string,
        onDelete: () => void,
    }) => {
    const [ showConfirm, setShowConfirm ] = useState(false);
    
    const handleYes = () => {
        setShowConfirm(false);
        props.onDelete();
    }
    
    const handleNo = () => {
        setShowConfirm(false)
    }
    
    return <>
        <ConfirmDialog show={showConfirm} message={`Are you sure you want to delete ${props.item.name}`} onNo={handleNo} onYes={handleYes} />
        <Button onClick={() => setShowConfirm(true)}><DeleteIcon /></Button>
    </>
}

const LibraryItem = (props: LibraryItemProps) => {
    const { item, path, navigate } = props;
    return <LibraryItemInner>
        <Link href="#" onClick={() => navigate(path)}>{item.name}</Link>
        <DeleteButton item={item} path={path} onDelete={props.onDelete} />
    </LibraryItemInner>
}

interface LibraryListProps {
    title: string;
    path: string;
    prefix: string
    items: SaveableBase[];
}

const LibraryList = (props: LibraryListProps) =>{
    const [items, setItems] = useState(props.items);

    const sortedNames = [ ...items ];
    sortedNames.sort((a,b) => {
        if (a.name < b.name) {
            return -1;
        }
        if (a.name > b.name) {
            return 1;
        }
        return 0;
    })

    const navigate = useNavigate();

    const deleteItem = (id: string) => {
        deleteLibraryData(props.prefix, id);
        setItems((current) => {
            return current.filter(i => i.id !== id)
        });
    }

    return <Section title={props.title} actions={[
        {
            action: () => navigate(props.path),
            label: <AddIcon></AddIcon>
        }
    ]}>
        {sortedNames.map((item) => <LibraryItem navigate={navigate} item={item} path={`${props.path}/${item.id}`} onDelete={() => deleteItem(item.id)} />)}
    </Section>
}

const LibraryCategory = (props: { name: string, path: string, data: any, onClick: () => void}) => {
    return <Grid item xs={4} onClick={props.onClick} style={{cursor: "pointer"}}>
        <Hexagon>
            <HexagonInner>
                <Box fontSize="55px"><HexagonIcon fontSize="inherit" color="primary" /></Box>
            </HexagonInner>
        </Hexagon>
        <Typography variant="body1" textAlign={"center"}>{props.name}</Typography>
    </Grid>
}

export const Library = () => {
    const navigate = useNavigate();
    const [selected, setSelected] = useState<null | string>(null);


    const weapons = listData("lw");
    const weaponMods = listData("lwm");
    const weaponVars = listData("lwv");

    const outfits = listData("lo");
    const outfitMods = listData("lom");
    const outfitVars = listData("lov");

    const equipments = listData("lmi");
    const utilities = listData("lut");
    const tradegoods = listData("tg");

    const companionsWeapons = listData("lcomwep");
    const companionsWeaponsMods = listData("lcomwepm");
    const companionsWeaponsVars = listData("lcomwepv");

    const traits = listData("ltr");
    const perks = listData("lpe");
    const complications = listData("lco");

    const species = listData("lspec");

    const packs = listData("lp");

    const libraryMap: { [k: string]: any } = {
        "lw": { name: "Weapons", path: "weapon", data: weapons},
        "lwv": { name: "Weapon Variations", path: "weaponVariation", data: weaponVars},
        "lwm": { name: "Weapon Mods", path: "weaponMod", data: weaponMods},
        "lo": { name: "Outfits", path: "outfit", data: outfits},
        "lov": { name: "Outfit Variations", path: "outfitVariation", data: outfitVars},
        "lom": { name: "Outfit Mods", path: "outfitMod", data: outfitMods},
        "lmi": { name: "Misc Items", path: "miscItem", data: equipments},
        "lut": { name: "Utilities", path: "utility", data: utilities},
        "lcomwep": { name: "Companion (Weapon)", path: "companionWeapon", data: companionsWeapons},
        "lcomwepv": { name: "Companion (Weapon) Variations", path: "companionWeaponVariation", data: companionsWeaponsVars},
        "lcomwepm": { name: "Companion (Weapon) Mods", path: "companionWeaponMod", data: companionsWeaponsMods},
        "lspec": { name: "Species", path: "species", data: species},
        "ltr": { name: "Traits", path: "trait", data: traits},
        "lpe": { name: "Perks", path: "perk", data: perks},
        "lco": { name: "Complications", path: "complication", data: complications},
        "lp": { name: "Packs", path: "pack", data: packs},
    }

    return <Page>
        <h2>Library</h2>
        <Stack direction={"row"} spacing={1} mb={2}>
            <Button variant="contained" onClick={() => navigate("/library/import")}>Import</Button>
            {selected !== null && <Button variant="contained" onClick={() => setSelected(null)}>Back</Button>}
        </Stack>

        {selected === null && <Grid container spacing={1} mt={2}>
            {Object.keys(libraryMap).map((key: string) => {
                return <LibraryCategory key={key} {...libraryMap[key]} onClick={() => setSelected(key)} />
            })}
        </Grid>}
        {selected !== null && <>
            <LibraryList items={libraryMap[selected].data} path={`/library/${libraryMap[selected].path}`} prefix={selected} title={libraryMap[selected].name} />
        </>}
    </Page>
}

export const LibraryRoute = () => {
    return <>
            <Routes>
                <Route path='weapon/:id?' element={<WeaponBlockEditorPage defaultName='Weapon' pathType='weapon' prefix='lw' isBase={true} />} />
                <Route path='weaponMod/:id?' element={<WeaponBlockEditorPage defaultName='Weapon Mod' pathType='weaponMod' prefix='lwm' isModifier={true} />} />
                <Route path='weaponVariation/:id?' element={<WeaponBlockEditorPage defaultName='Weapon Variation' pathType='weaponVariation' prefix='lwv' isModifier={true} />} />
                <Route path='outfit/:id?' element={<OutfitBlockEditorPage defaultName='Outfit' pathType='outfit' prefix='lo' />} />
                <Route path='outfitMod/:id?' element={<OutfitBlockEditorPage defaultName='Outfit Mod' pathType='outfitMod' prefix='lom'  isModifier={true} />} />
                <Route path='outfitVariation/:id?' element={<OutfitBlockEditorPage defaultName='Outfit Variation' pathType='outfitVariation' prefix='lov' isModifier={true} />} />
                <Route path='miscItem/:id?' element={<EquipmentEditor defaultName='New Misc Item' pathType='miscItem' prefix='lmi' />} />
                <Route path='utility/:id?' element={<EquipmentEditor defaultName='New Utility' pathType='utility' prefix='lut' />} />
                <Route path='trait/:id?' element={<TraitEditor defaultName='New Trait' pathType='trait' prefix='ltr' />} />
                <Route path='perk/:id?' element={<TraitEditor defaultName='New Perk' pathType='perk' prefix='lpe' />} />
                <Route path='complication/:id?' element={<TraitEditor defaultName='New Complication' pathType='complication' prefix='lco' />} />
                <Route path='companionWeapon/:id?' element={<CompanionWeaponPage defaultName='Companion (Weapon)' isbase pathType="companionWeapon" prefix="lcomwep" />} />
                <Route path='companionWeaponVariation/:id?' element={<CompanionWeaponPage defaultName='Companion (Weapon Variation)' pathType="companionWeaponVariation" prefix="lcomwepv" />} />
                <Route path='companionWeaponMod/:id?' element={<CompanionWeaponPage defaultName='Companion (Weapon Mod)' pathType="companionWeaponMod" prefix="lcomwepm" />} />
                <Route path='species/:id?' element={<SpeciesEditor />} />
                <Route path='import' element={<Import />} />
                <Route path='pack/:id?' element={<PackManager />} />
                <Route path='/' element={<Library />} />
            </Routes>
    </>
}